//--------------------------
// Constants
//--------------------------
const DB_NAME = 'db01'

//--------------------------
// Models
//--------------------------
class Store {
  constructor(name, db) {
    this.name = name
    this.db = db
    if (![...db.objectStoreNames].some(n => n == name)) {
      db.createObjectStore(name, { keyPath: 'guid' }) // Datatoreの keyPathは guid固定
    }
  }

  get objectStore() {
    return this.db.transaction([this.name]).objectStore(this.name)
  }
  get objectStoreWritable() {
    return this.db.transaction([this.name], 'readwrite').objectStore(this.name)
  }

  count(keyPath) {
    return new Promise((resolve, reject) => {
      let request = this.objectStore.count(keyPath)
      request.onsuccess = event => {
        resolve(event.target.result)
      }
      request.onerror = event => {
        console.log('[ERROR] count: ', event)
        reject(event)
      }
    })
  }

  get(keyPath) {
    return new Promise((resolve, reject) => {
      let request = keyPath
        ? this.objectStore.get(keyPath)
        : this.objectStore.getAll()
      request.onsuccess = event => {
        resolve(event.target.result)
      }
      request.onerror = event => {
        console.log('[ERROR] get: ', event)
        reject(event)
      }
    })
  }

  put(args) {
    let objs = Array.isArray(args) ? args : [args]

    return new Promise((resovle, reject) => {
      objs.forEach((obj, index) => {
        let request = this.objectStoreWritable.put(
          Object.assign(obj, { cached: new Date().getTime() })
        )
        // objStore.onsuccessや tran.oncomplete は呼び出されず。仕方なく１件づつハンドリング
        request.onsuccess = () => {
          if (objs.length - 1 == index) {
            resovle(objs)
          }
        }
        request.onerror = event => {
          console.log('[ERROR] put', event)
          reject(event)
        }
      })
    })
  }

  delete(keyPath) {
    return new Promise((resolve, reject) => {
      let request = this.objectStoreWritable.delete(keyPath)
      request.onsuccess = event => {
        resolve(event.target.result)
      }
      request.onerror = event => {
        console.log('[ERROR] delete: ', event)
        reject(event)
      }
    })
  }

  clear() {
    return new Promise((resolve, reject) => {
      const request = this.objectStoreWritable.clear()
      request.onsuccess = () => {
        resolve()
      }
      request.onerror = event => {
        console.log('[ERROR] clear', event)
        reject(event)
      }
    })
  }
}

//--------------------------
// サービス本体
//--------------------------
class IndexedDBService {
  constructor() {
    this.callback = undefined // 初期化後に呼び出される
    this.initialized = false
  }

  _waitWithTimeout() {
    return new Promise(resolve => {
      setTimeout(() => resolve(), 100)
    })
  }

  async wait() {
    let timeoutCounter = 50
    while (!this.initialized) {
      // console.log('wating...')
      await this._waitWithTimeout()
      if (--timeoutCounter <= 0) {
        // console.log('--timeout...')
        return
      }
    }
    // console.log('**done**')
  }

  setup() {
    if (this.db) {
      return new Promise(resolve => {
        // console.log('[INFO] indexedDB has been opened')
        resolve(this.db)
      })
    }
    // console.log('[INFO] initialize indexedDB')
    const initStores = event => {
      this.db = event.target.result
      console.log(`indexedDB: name=${this.db.name}, version=${this.db.version}`)
      //----------------------
      // ** 宣言はここで行う **
      //----------------------
      // Version 1
      this.notes = new Store('notes', this.db)
      this.notebooks = new Store('notebooks', this.db)
      this.tags = new Store('tags', this.db)
      this.resources = new Store('resources', this.db)
      // Version 2
      this.conditions = new Store('conditions', this.db)
      // Version 3
      this.thumbnails = new Store('thumbnails', this.db)
      // Version 4
      // this.noteimages = new Store('noteimages', this.db) // 不採用
      // Version 5
      this.notemetas = new Store('notemetas', this.db) // アプリ独自のnote情報
      // Version 6
      this.relatednotes = new Store('relatednotes', this.db) // 関係するnote
      // // Version 7
      // this.relatedhistories = new Store('relatedhistories', this.db) // 関係するnote // 不採用
      // Version 8
      this.noteviews = new Store('noteviews', this.db)
      // console.log(this)
      // console.log('indexedDB: initialized')
      this.initialized = true
      if (this.callback) {
        this.callback()
      }
    }

    return new Promise((resolve, reject) => {
      // console.log('[INFO] opening idexedDB ...')
      const request = indexedDB.open(DB_NAME, 8, upgradeDb => {
        console.log('indexedDB: opened')
        if (upgradeDb.oldVersion < 2) {
          console.log('indexedDB: upgrade 1 to 2')
          new Store('conditions', upgradeDb)
        }
        if (upgradeDb.oldVersion < 3) {
          console.log('indexedDB: upgrade 2 to 3')
          new Store('thumbnails', upgradeDb)
        }
        // if (upgradeDb.oldVersion < 4) {
        //   console.log('indexedDB: upgrade 3 to 4')
        //   new Store('noteimages', upgradeDb)
        // }
        if (upgradeDb.oldVersion < 5) {
          console.log('indexedDB: upgrade 3 to 5')
          new Store('notemetas', upgradeDb)
        }
        if (upgradeDb.oldVersion < 6) {
          console.log('indexedDB: upgrade 5 to 6')
          new Store('relatednotes', upgradeDb)
        }
        // if (upgradeDb.oldVersion < 7) {
        //   console.log('indexedDB: upgrade 6 to 7')
        //   new Store('relatedhistories', upgradeDb)
        // }
        if (upgradeDb.oldVersion < 8) {
          console.log('indexedDB: upgrade 6 to 8')
          new Store('noteviews', upgradeDb)
        }
      })
      request.onupgradeneeded = initStores
      request.onsuccess = event => {
        initStores(event)
        resolve(event.target.result)
      }
      request.onerror = event => {
        console.log('[ERROR] ' + event.target.errorCode)
        reject('fails to open db')
      }
    })
  }

  //-----------------
  // Clear All Stores
  //-----------------
  clearAllStores() {
    return Promise.all(
      [...this.db.objectStoreNames].map(storeName => {
        new Promise((resolve, reject) => {
          const request = this.db
            .transaction([storeName], 'readwrite')
            .objectStore(storeName)
            .clear()
          request.onsuccess = event => {
            resolve(event)
          }
          request.onerror = event => {
            reject(event)
          }
        })
      })
    )
  }
  //-----------------
  // Delete All Stores
  //-----------------
  deleteDB() {
    return new Promise((resolve, reject) => {
      // console.log('close db')
      this.db.close()
      // console.log('delete db')
      const request = indexedDB.deleteDatabase(DB_NAME)
      request.onsuccess = () => {
        this.db = null
        resolve()
      }
      request.onerror = event => {
        console.log('[ERROR] delete', event)
        reject(event)
      }
    })
  }
}
const indexedDBService = new IndexedDBService()
export default indexedDBService
